import type { OrgEntity } from "../../domain/entities/OrgEntity.js";
import type {
  IOrgRepository,
  OrgCreateInput,
  OrgUpdateInput,
} from "../../domain/repositories/IOrgRepository.js";
import {
  OrgRemoteDataSource,
  type OrgRemoteListOptions,
  type OrgRemoteCreatePayload,
  type OrgRemoteUpdatePayload,
} from "../datasources/OrgRemoteDataSource.js";
import type { OrgDto } from "../dtos/OrgDto.js";

function mapDtoToEntity(dto: OrgDto): OrgEntity {
  return {
    id: dto.id,
    slug: dto.slug,
    name: dto.name,
    description: dto.description ?? null,
    createdBy: dto.createdBy,
    createdAt: dto.createdAt ?? null,
  };
}

export class OrgRepository implements IOrgRepository {
  constructor(private readonly remoteDataSource: OrgRemoteDataSource) {}

  async getById(id: string): Promise<OrgEntity | null> {
    const dto = await this.remoteDataSource.getOrgById(id);
    return dto ? mapDtoToEntity(dto) : null;
  }

  async list(options?: OrgRemoteListOptions): Promise<OrgEntity[]> {
    const dtos = await this.remoteDataSource.listOrgs(options);
    return dtos.map(mapDtoToEntity);
  }

  async create(input: OrgCreateInput): Promise<OrgEntity> {
    const payload: OrgRemoteCreatePayload = {
      name: String(input.name).trim(),
      slug: input.slug,
      description: input.description ?? null,
      createdBy: String(input.createdBy),
    };
    const dto = await this.remoteDataSource.createOrg(payload);
    if (!dto) throw new Error("Org creation failed");
    return mapDtoToEntity(dto);
  }

  async update(id: string, input: OrgUpdateInput): Promise<OrgEntity | null> {
    const payload: OrgRemoteUpdatePayload = {
      name: input.name,
      slug: input.slug,
      description: input.description ?? null,
    };
    const dto = await this.remoteDataSource.updateOrg(id, payload);
    return dto ? mapDtoToEntity(dto) : null;
  }

  async delete(id: string): Promise<void> {
    await this.remoteDataSource.deleteOrg(id);
  }

  async existsBySlug(slug: string, excludeId?: string): Promise<boolean> {
    return this.remoteDataSource.checkSlugExists(slug, excludeId);
  }

  async transferOwner(orgId: string, newOwnerId: string): Promise<void> {
    await this.remoteDataSource.transferOrgOwner(orgId, newOwnerId);
  }
}
